import { useContext, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Toolbar,
  Button,
  MenuList,
  MenuItem,
  Typography,
  ThemeProvider,
} from "@mui/material";

import ChildSelector from "../ChildSelector";
import Logo from "@/assets/icons/pinwheel-logo-icon-blue.svg?react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGrid,
  faAddressBook,
  faMessageLines,
  faMobileScreenButton,
  faCircleCheck,
  faCalendarDays,
  faMessageQuestion,
  faStar,
  faPlus,
  faPhone,
} from "@fortawesome/pro-light-svg-icons";
import { pinwheelPalette } from "../../styles/theme";

import "./navigation.sass";
import { NotificationDot } from "../NotificationDot";
import { useGetContacts } from "@/routes/new-contacts/hooks";
import { SafeListMode } from "@/openapi";
import ChildContext from "@/context/child";
import { useIsPinwheelHomeChild, useIsWatchChild, useWhatsNewState } from "@/hooks";
import { typographyTheme } from "@/styles/customTheme";
import PinwheelAskIcon from "@/assets/icons/pinwheel-ask-icon.svg?react";
import { useContactPermissions, usePhoneHistoryPermissions, usePinwheelAskPermissions, useAppsScheduleModesRoutinesPermissions } from "@/util/permission";
import { useTranslation } from "react-i18next";

const Navigation = () => {
  const [isNewVersion, setIsNewVersion] = useState(true);
  const { child } = useContext(ChildContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { pendingContacts } = useGetContacts();
  const isWatchChild = useIsWatchChild();
  const isPinwheelHomeChild = useIsPinwheelHomeChild(child);
  const { showNotification, onLinkClicked } = useWhatsNewState();
  const { noAccess: noContactAccess } = useContactPermissions();
  const { noAccess: hasNoPinwheelAskAccess } = usePinwheelAskPermissions();
  const { noAccess: hasNoAppAccess } = useAppsScheduleModesRoutinesPermissions();
  const { noAccess: hasNoPhoneHistoryAccess } = usePhoneHistoryPermissions();
  const { t } = useTranslation("common");

  if (isNewVersion) {
     
    localStorage.setItem("appVersion", import.meta.env.VITE_VERSION!);
    setTimeout(() => {
      setIsNewVersion(false);
    }, 3000);
  }

  const handleClick = () => {
    navigate(
      {
        pathname: "/sign-up-child"
      },
      {
        state: {
          fromDashboard: true
        }
      }
    );
  };


  const drawer = (
    <div>
      <Toolbar disableGutters={true} sx={{ my: 2, mx: 0 }}>
        <NavLink className="logo" to="/">
          <Logo />
        </NavLink>
      </Toolbar>
      <List className="button-holder">
        <ListItem>
          <ChildSelector />
        </ListItem>
        <ListItem>
          <Button endIcon={<FontAwesomeIcon icon={faPlus} />} onClick={handleClick} size="large" fullWidth>
            <Typography variant="body1" color={pinwheelPalette.white[100]} lineHeight={1.7}>
              {t("addChild")}
            </Typography>
          </Button>
        </ListItem>
      </List>
      <Divider sx={{ borderColor: pinwheelPalette.lightGrey[100], height: "1px" }} />
      <MenuList className="list-menu" data-cy="navMenuList">
        {!isPinwheelHomeChild ? (
          <MenuItem className={`${location.pathname === "/dashboard" ? "active" : ""}`}>
            <Box className="menu-item-container" display="flex" alignItems="center">
              <NavLink to="/dashboard">
                <ListItemIcon>
                  <FontAwesomeIcon icon={faGrid} />
                </ListItemIcon>
                <ListItemText>{t("dashboard")}</ListItemText>
              </NavLink>
            </Box>
          </MenuItem>
        ) : null}
        {!noContactAccess ? (
          <MenuItem className={`${location.pathname.includes("/contacts") ? "active" : ""}`}>
            <Box className="menu-item-container" display="flex" alignItems="center">
              <NavLink to="/contacts">
                <ListItemIcon>
                  <FontAwesomeIcon icon={faAddressBook} />
                </ListItemIcon>
                <ListItemText>{t("contacts")}</ListItemText>
                {child?.configuration.safelistMode === SafeListMode.Approved && pendingContacts.length > 0 ? (
                  <NotificationDot
                    sx={{
                      fontSize: pendingContacts.length > 9 ? "10px!important" : "12px",
                    }}
                  >
                    {pendingContacts.length > 9 ? "9+" : pendingContacts.length}
                  </NotificationDot>
                ) : null}
              </NavLink>
            </Box>
          </MenuItem>
        ) : null}
        {
          !hasNoPhoneHistoryAccess ? (
            <MenuItem className={`${location.pathname.includes("/phone-history") ? "active" : ""}`}>
              <Box className="menu-item-container" display="flex" alignItems="center">
                <NavLink to="/phone-history">
                  <ListItemIcon>
                    <FontAwesomeIcon icon={isPinwheelHomeChild ? faPhone : faMessageLines} />
                  </ListItemIcon>
                  <ListItemText>{isPinwheelHomeChild ? t("callHistory", { ns: "conversations" }) : t("phoneHistory")}</ListItemText>
                </NavLink>
              </Box>
            </MenuItem>
          ) : null
        }
        {!hasNoAppAccess && !isPinwheelHomeChild ? (
          <MenuItem className={`${location.pathname.includes("/apps") ? "active" : ""}`}>
            <Box className="menu-item-container" display="flex" alignItems="center">
              <NavLink to="/apps">
                <ListItemIcon>
                  <FontAwesomeIcon icon={faMobileScreenButton} />
                </ListItemIcon>
                <ListItemText>{t("apps")}</ListItemText>
              </NavLink>
            </Box>
          </MenuItem>
        ) : null}
        {!isPinwheelHomeChild && !isWatchChild && !hasNoAppAccess ? (
          <MenuItem className={`${location.pathname === "/routines" ? "active" : ""}`}>
            <Box className="menu-item-container" display="flex" alignItems="center">
              <NavLink to="/routines">
                <ListItemIcon>
                  <FontAwesomeIcon icon={faCircleCheck} />
                </ListItemIcon>
                <ListItemText>{t("routines", { ns: "routine" })}</ListItemText>
              </NavLink>
            </Box>
          </MenuItem>
        ) : null}
        {!hasNoAppAccess ? (
          <MenuItem className={`${location.pathname === "/calendar" ? "active" : ""}`}>
            <Box className="menu-item-container" display="flex" alignItems="center">
              <NavLink to="/calendar">
                <ListItemIcon>
                  <FontAwesomeIcon icon={faCalendarDays} />
                </ListItemIcon>
                <ListItemText>{t("schedule")}</ListItemText>
              </NavLink>
            </Box>
          </MenuItem>
        ) : null}
        {!isPinwheelHomeChild && !hasNoPinwheelAskAccess ? (
          <MenuItem className={`${location.pathname.startsWith("/pinwheel-gpt") ? "active" : ""}`}>
            <Box className="menu-item-container" display="flex" alignItems="center">
              <NavLink to="/pinwheel-gpt">
                <ListItemIcon>
                  <PinwheelAskIcon />
                </ListItemIcon>
                <ListItemText>{"PinwheelAsk"}</ListItemText>
              </NavLink>
            </Box>
          </MenuItem>
        ) : null}
        <Divider sx={{ borderColor: pinwheelPalette.lightGrey[100], height: "1px" }} />
        <MenuItem>
          <Box className="menu-label-container" display="flex" alignItems="center">
            <ListItemText slotProps={{ primary: { variant: "body5" } }}>{t("support")}</ListItemText>
          </Box>
        </MenuItem>
        <MenuItem>
          <Box className="menu-item-container" display="flex" alignItems="center">
            <Link underline="none" href="https://support.pinwheel.com/" target="_blank" rel="noreferrer">
              <ListItemIcon className="menu-icons">
                <FontAwesomeIcon icon={faMessageQuestion} />
              </ListItemIcon>
              <ListItemText>{t("help")}</ListItemText>
            </Link>
          </Box>
        </MenuItem>
        <MenuItem>
          <Box className="menu-item-container" display="flex" alignItems="center">
            <Link underline="none" onClick={onLinkClicked} href="https://www.pinwheel.com/whats-new" target="_blank" rel="noreferrer">
              <ListItemIcon className="menu-icons">
                <FontAwesomeIcon icon={faStar} />
              </ListItemIcon>
              <ListItemText>{t("whatsNew")}</ListItemText>
              {showNotification ? <NotificationDot /> : null}
            </Link>
          </Box>
        </MenuItem>
      </MenuList>
    </div>
  );

  return (
    <ThemeProvider theme={typographyTheme}>
      <Box
        component="nav"
        aria-label="mailbox folders"
        className="navbar"
        sx={{
          borderBottom: `1px solid ${pinwheelPalette.grey[5]}`,
          minHeight: { lg: "100vh" }
        }}
      >
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", lg: "block" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", position: "absolute", right: 0, borderColor: pinwheelPalette.lightGrey[100] }
          }}
        >
          {drawer}
        </Drawer>

      </Box>
    </ThemeProvider>
  );
};

export default Navigation;
